import { createNavigator, SwitchRouter } from '@react-navigation/core';
import { createBrowserApp } from '@react-navigation/web';
import React, { useEffect } from 'react';
import ENVIRONMENT_VARIABLES from 'shared/utils/environmentVariables';
import SmartBanner from './components/SmartBanner';
import { EVENT_TYPES, TARGET_TYPES } from './lib/analytics/constants';
import { Logger } from './lib/analytics/logger';

/* Navigation Wrapper */
import AuthSwitch from './components/AuthSwitch/AuthSwitch';
import LobbyOverlay from './components/LobbyOverlay';

/* Screens Imports */
import Screens from 'shared/screens';
import FinishPurchase from './components/WebFinishPurchase';
import Favourites from './screens/Favourites';
import ForgotPassword from './screens/ForgotPassword';
import Game from './screens/Game';
import Home from './screens/Home';
import Login from './screens/Login/Login';
import Missions from './screens/Missions';
import SessionExpired from './screens/SessionExpired';
import Signup from './screens/Signup';
import Start from './screens/Start';
import TableGames from './screens/TableGames';
import DoubleOptIn from './screens/DoubleOptIn';
import TrackingURL from './screens/TrackingURL';
import VIPGames from './screens/VIPGames';
import Events from './screens/Events/Events';
import RewardCenter from './screens/RewardCenter';
import SportGames from './screens/SportGames/SportGames';
import Battles from './screens/Battles/Battles';

/* Utils */
import Utils from 'shared/utils';

/* Store and data */
import { connect } from 'shared/node_modules/react-redux';
import { useLazyGetProgressiveJackpotsQuery } from 'shared/state/query/progressiveJackpots';

/* Query String Stuff */

/** Notifications */
import { PushNotification } from './lib/notifications/PushNotification';

// CSS
import './App.scss';

// Hooks
import useFeatureFlags from 'shared/hooks/useFeatureFlags';

// Constants
import { GET_PROGRESSIVE_JACKPOTS_SCREENS } from 'shared/constants';
import { FE_PROGRESSIVE_JACKPOTS } from 'shared/utils/featureFlags/constants';
import navigation from 'shared/utils/navigation/navigation';
import QueryParamProxy from 'shared/utils/queryParamProxy';

// Init QPP
QueryParamProxy.init();

const LobbyStack = createNavigator(
  LobbyOverlay,
  SwitchRouter(
    {
      [Screens.Home]: { screen: Home, path: 'slots' },
      [Screens.Game]: { screen: Game, path: `game/:gid` },
      [Screens.Favourites]: { screen: Favourites, path: 'favourites' },
      [Screens.Tables]: { screen: TableGames, path: 'tables' },
      [Screens.VIP]: { screen: VIPGames, path: 'vip' },
      [Screens.Sports]: { screen: SportGames, path: 'sports' },
      [Screens.Missions]: { screen: Missions, path: 'missions' },
      [Screens.Events]: { screen: Events, path: 'Events' },
      [Screens.RewardCenter]: { screen: RewardCenter, path: 'RewardCenter' },
      [Screens.Battles]: { screen: Battles, path: 'battles' },
    },
    {
      backBehavior: 'history',
    }
  ),
  {
    initialRouteName: Screens.Home,
  }
);

const screens = {
  [Screens.Start]: Start,
  [Screens.SessionExpired]: SessionExpired,
  [Screens.Login]: Login,
  [Screens.ForgotPassword]: ForgotPassword,
  [Screens.Signup]: Signup,
  [Screens.DoubleOptIn]: {
    screen: DoubleOptIn,
    path: 'emailverification/:pev',
  },
  [Screens.TrackingURL]: {
    screen: TrackingURL,
    path: 'externallanding/:aff_id',
    parse: {
      aff_id: aff_id => aff_id.replace(/^@/, ''),
    },
  },
  [Screens.Lobby]: {
    screen: LobbyStack,
    path: 'home',
  },
  [Screens.FinishWebPurchase]: {
    screen: FinishPurchase,
  },
};

const RootStack = createNavigator(AuthSwitch, SwitchRouter(screens), {
  initialRouteName: Screens.Start,
});

const App = createBrowserApp(RootStack);

let loggerTargets = [
  {
    name: TARGET_TYPES.DIVE,
  },
  {
    name: TARGET_TYPES.FACEBOOK_PIXEL,
  },
];

if (ENVIRONMENT_VARIABLES.XTREMEPUSH_ID) {
  loggerTargets.push({
    name: TARGET_TYPES.XTREMEPUSH,
  });
}
PushNotification.initialize();

const mapStateToProps = state => {
  return {
    featureFlags: state.featureFlags.enabledFeatures,
  };
};

const AppComponent = props => {
  const { featureFlags } = props;
  const { hasFeature } = useFeatureFlags();
  const hasProgressiveJackpots = hasFeature(FE_PROGRESSIVE_JACKPOTS);
  Logger.initialize({ targets: loggerTargets });

  const [getProgressiveJackpots] = useLazyGetProgressiveJackpotsQuery();

  useEffect(() => {
    hasProgressiveJackpots && getProgressiveJackpots();
  }, [featureFlags, hasProgressiveJackpots]);

  return (
    <>
      <App
        ref={navigatorRef => {
          navigation.setTopLevelNavigator(navigatorRef);
        }}
        onNavigationStateChange={(prevState, currentState) => {
          const currentRouteName = Utils.getActiveRouteName(currentState);

          // request jackpots when navigating to a screen where these games can be seen and the feature is enabled
          if (GET_PROGRESSIVE_JACKPOTS_SCREENS.includes(currentRouteName) && hasProgressiveJackpots)
            getProgressiveJackpots();

          Logger.sendEvent(EVENT_TYPES.VIEW);
        }}
      />
      <SmartBanner />
    </>
  );
};

export default connect(mapStateToProps)(AppComponent);

